import React, {Component} from 'react';
import DateTimeFormatter from '../../helpers/DateTimeFormatter';
import Segment, {SEGMENT_DIRECTION} from './Segment';
import IframeHelper from '../../helpers/IframeHelper';

export default class FlightDetails extends Component {
    direction = {
        FORWARD: 'forward',
        BACK: 'back',
        RETURN: 'return'
    };

    isDirect(direction) {
        if (direction === this.direction.BACK || direction === this.direction.RETURN) {
            return this.props.flight.returnSector.stops === 0;
        }

        return this.props.flight.forwardSector.stops === 0;
    }

    renderSectorStops(sector, type) {
        if (this.isDirect(type)) {
            return this.translator.trans('Direct');
        } else if (sector.stops === 1) {
            return sector.stops + ' ' + this.translator.trans('stop');
        } else {
            return sector.stops + ' ' + this.translator.trans('stops');
        }
    }

    renderFlightDuration(sector, type) {
        const duration = DateTimeFormatter.secsToTimeParts(sector.duration);
        return (
            <span className="ff-dir-meta">
                <span className={`${this.isDirect(type) ? 'text-success' : 'text-danger'}`}>
                    {this.renderSectorStops(sector, type)}
                </span>
                <span>·</span>
                <span>
                    {this.translator.trans('Travel duration')}:{` `}
                    <span className="num">
                        {DateTimeFormatter.timeFormat(
                            duration[0],
                            duration[1],
                            this.translator.trans('h.'),
                            this.translator.trans('m.'))
                        }
                    </span>
                </span>
            </span>
        );
    }

    renderDirectionPlane(isReturn) {
        const planePath = 'M17.8 19.2 16 11l3.5-3.5c1-1 1.5-2.4 1.5-3.5a2.12 2.12 0 0 0-2-2c-1.1 0-2.5.5-3.5 1.5L12 8 3.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z';
        return (
            <svg
                className="ff-dir-plane"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={isReturn ? {transform: 'scaleX(-1)'} : undefined}
                aria-hidden="true"
            >
                <path d={planePath}/>
            </svg>
        );
    }

    renderTransferTime(segments, stopNr) {
        const departure = segments[stopNr].departureTime;
        const arrival = segments[stopNr - 1].arrivalTime;
        const next = DateTimeFormatter._parseDate(departure).getTime();
        const prev = DateTimeFormatter._parseDate(arrival).getTime();
        const diff = DateTimeFormatter.getTimeDiff(next, prev);

        return (
            <span className="small align-text-top">
                {this.translator.trans('Airport transfer time')}:
                <strong className="ml-1">
                    {DateTimeFormatter.timeFormat(
                        diff.hours,
                        diff.minutes,
                        this.translator.trans('h.'),
                        this.translator.trans('m.'))
                    }
                    <span className="ml-1">{segments[stopNr].departureCityName},</span>
                    <span className="ml-1">
                        {segments[stopNr].departureAirportName} ({segments[stopNr].departureAirport}), {segments[stopNr].departureCountryName}
                    </span>
                </strong>
            </span>
        );
    }

    renderSegmentList(segments, type) {
        return segments.map((segment, index) => {
            let transfer = null;
            if (index !== 0) {
                transfer = (
                    <div className="alert alert-warning mb-0">
                        <span className="icon icon-change text-warning"/> {this.renderTransferTime(segments, index)}
                    </div>
                );
            }

            let direction = SEGMENT_DIRECTION.SEGMENT_DIRECT;
            if (!this.isDirect(type)) {
                if (index === 0) {
                    direction = SEGMENT_DIRECTION.SEGMENT_STOP_START;
                } else if (index === segments.length - 1) {
                    direction = SEGMENT_DIRECTION.SEGMENT_STOP_END;
                } else {
                    direction = SEGMENT_DIRECTION.SEGMENT_STOP;
                }
            }

            let separatorNeeded = this.isSeparatingLineNeeded(segments, index, type);

            return (
                <div key={index}>
                    {transfer}
                    <Segment key={index} segment={segment} translator={this.translator} direction={direction}
                             separatorNeeded={separatorNeeded}/>
                </div>
            );
        });
    }

    isSeparatingLineNeeded(segments, index, type) {
        return segments.length === index + 1
            && type !== 'return'
            && this.flight.returnSegments
            && this.flight.returnSegments.length > 0;
    }

    renderForwardSegmentHeader() {
        return (
            <div className="direction">
                {this.renderDirectionPlane(false)}
                <span className="ff-dir-route">
                    {this.flight.forwardSector.departureAirportName} → {this.flight.forwardSector.arrivalAirportName}
                </span>
                <span className="ff-dir-date num">
                    · {DateTimeFormatter.getDate(this.flight.forwardSector.departureTime, this.dateFormat)}
                </span>
                {this.renderFlightDuration(this.flight.forwardSector)}
            </div>
        );
    }

    renderReturnSegmentHeader() {
        if (!this.flight.returnSegments || this.flight.returnSegments.length === 0) {
            return null;
        }

        return (
            <div className="direction">
                {this.renderDirectionPlane(true)}
                <span className="ff-dir-route">
                    {this.flight.returnSector.departureAirportName} → {this.flight.returnSector.arrivalAirportName}
                </span>
                <span className="ff-dir-date num">
                    · {DateTimeFormatter.getDate(this.flight.returnSector.departureTime, this.dateFormat)}
                </span>
                {this.renderFlightDuration(this.flight.returnSector, this.direction.RETURN)}
            </div>
        );
    }

    renderAllPassengersPrice() {
        let label = <div className="small">{this.translator.trans('For all passengers')}:</div>;
        if (this.props.isOnePassenger) {
            label = '';
        }
        return (
            <div className="float-right text-xs-right">
                {label}
                <h2 className="font-weight-bold mb-0">
                    <span {...this.priceFormatter.formatParts(this.flight.general.price)}/></h2>
            </div>
        );
    }

    toggleCollapse() {
        this.props.state.collapse = !this.props.state.collapse;
    }

    componentDidUpdate() {
        if (this.props.state.collapse) {
            IframeHelper.updateHeight(this.props.projectUrl);
        }
    }

    render() {
        this.translator = this.props.translator;
        this.flight = this.props.flight;
        this.priceFormatter = this.props.priceFormatter;
        this.dateFormat = this.props.dateFormat;

        return (
            <div {...(this.props.state.collapse ? {} : {hidden: 'hidden'})}>
                <div className="detailed-flight">
                    <div className="subheader clearix">
                        <strong>{this.translator.trans('Flight information')}</strong>
                        <button type="button" data-toggle="collapse" onClick={this.props.toggleCollapse}
                                className="close float-right" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    {this.renderForwardSegmentHeader()}
                    <div className="ff-seg-card">
                        {this.renderSegmentList(this.flight.forwardSegments, this.direction.FORWARD)}
                    </div>
                    {this.renderReturnSegmentHeader()}
                    {this.flight.returnSegments &&
                        <div className="ff-seg-card">
                            {this.renderSegmentList(this.flight.returnSegments, this.direction.RETURN)}
                        </div>
                    }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/flights/FlightDetails.js